/**
 * A-03 承認履歴。過去の承認・差戻しを一覧で確認する。
 */

import { useState } from 'react';
import { endpoints } from '../../api/endpoints.js';
import { MonthNav } from '../../components/MonthNav.js';
import { Card, EmptyState, ErrorMessage, Loading } from '../../components/ui.js';
import { currentMonth, formatDateLabel, formatDateTime } from '../../lib/format.js';
import { useAsync } from '../../lib/useAsync.js';

export function ApprovalHistoryPage(): React.JSX.Element {
  const [month, setMonth] = useState(currentMonth());
  const [decision, setDecision] = useState<'all' | 'approve' | 'reject'>('all');
  const state = useAsync(() => endpoints.approvalHistory(month), [month]);

  const items = (state.data?.items ?? []).filter(
    (item) => decision === 'all' || item.action === decision,
  );

  return (
    <>
      <MonthNav
        month={month}
        onChange={setMonth}
        right={
          <select
            className="input w-auto"
            value={decision}
            onChange={(event) => setDecision(event.target.value as 'all' | 'approve' | 'reject')}
            aria-label="判断で絞り込む"
          >
            <option value="all">すべての判断</option>
            <option value="approve">承認のみ</option>
            <option value="reject">差戻しのみ</option>
          </select>
        }
      />

      {state.error !== null && <ErrorMessage error={state.error} />}
      {state.loading && !state.data && <Loading />}

      {state.data && (
        <Card title={`承認履歴 ${items.length}件`}>
          {items.length === 0 ? (
            <EmptyState message="この月の承認履歴はありません。" />
          ) : (
            <table className="table">
              <thead>
                <tr>
                  <th>判断日時</th>
                  <th>スタッフ</th>
                  <th>所属部門</th>
                  <th>対象日</th>
                  <th>判断</th>
                  <th>コメント</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item, index) => (
                  <tr key={`${item.decidedAt}-${index}`}>
                    <td className="whitespace-nowrap">{formatDateTime(item.decidedAt)}</td>
                    <td>{item.employeeName ?? '—'}</td>
                    <td className="text-ink-muted">{item.department ?? '—'}</td>
                    <td className="whitespace-nowrap">
                      {item.date ? formatDateLabel(item.date) : '—'}
                    </td>
                    <td>
                      <span
                        className={`pill ${item.action === 'approve' ? 'pill--ok' : 'pill--warn'}`}
                      >
                        {item.action === 'approve' ? '承認' : '差戻し'}
                      </span>
                    </td>
                    <td className="text-ink-sub">{item.comment || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>
      )}
    </>
  );
}
